import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface StatCardProps {
  label: string;
  value: string | number;
  subtext?: string;
  icon?: LucideIcon;
  trend?: { value: string; positive?: boolean };
  accent?: "orange" | "green" | "navy" | "amber";
  className?: string;
}

const accentStyles = {
  orange: "border-l-orange-construction",
  green: "border-l-green-approved",
  navy: "border-l-navy-blueprint",
  amber: "border-l-amber-warning",
} as const;

export function StatCard({
  label,
  value,
  subtext,
  icon: Icon,
  trend,
  accent = "navy",
  className,
}: StatCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg border border-l-4 bg-card-white p-4 shadow-[var(--shadow-card)]",
        accentStyles[accent],
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        {Icon ? (
          <Icon className="h-4 w-4 shrink-0 text-navy-blueprint/60" aria-hidden />
        ) : null}
      </div>
      <p className="mt-1 text-2xl font-bold tabular-nums text-navy-deep">{value}</p>
      {(subtext || trend) && (
        <div className="mt-1 flex items-center gap-2">
          {trend ? (
            <span
              className={cn(
                "text-xs font-medium",
                trend.positive ? "text-green-approved" : "text-red-danger",
              )}
            >
              {trend.value}
            </span>
          ) : null}
          {subtext ? <span className="text-xs text-muted-foreground">{subtext}</span> : null}
        </div>
      )}
    </div>
  );
}
